/**
 * Admin/QueueMapMonitor/Index.jsx
 *
 * Live interactive three-scope passenger position map.
 * Uses Leaflet.js (loaded via CDN), L.divIcon SVG markers, and OpsMonitorChannel
 * ActionCable for real-time QUEUE_SESSION_POSITION_UPDATE events.
 *
 * Three scope tabs:
 *   🌍 World   — air/flying, great-circle paths
 *   🏙 City    — surface transport: driving, cycling, motorcycle, rideshare, etc.
 *   🚶 Property — indoor walking/PRM paths at JFK T5 anchor nodes
 *
 * Layout: AdminLayout sidebar is applied automatically via application.js resolve().
 *
 * Props (from Admin::QueueMapMonitorController#index):
 *   active_sessions: Array<SerializedMapSession>   — sessions with valid JourneyPosition
 *   tenant_key: string                             — for ActionCable subscription filter
 */

import { useState, useEffect, useRef, useCallback } from "react";
import WorldMapView        from "./WorldMapView.jsx";
import CityMapView         from "./CityMapView.jsx";
import PropertyMapView     from "./PropertyMapView.jsx";
import CesiumPropertyView, { isCesiumVenue } from "./CesiumPropertyView.jsx";
// Snap air-scope positions to their great-circle arc on every ActionCable update,
// not only on initial page load (where WorldMapView applies the snap itself).
import { snapAirPositionToArc } from "../../../utils/JourneyQueueMapLibre.js";

const SCOPES = [
  { key: "air",      label: "🌍 World",    description: "Air / Flights" },
  { key: "city",     label: "🏙 City",     description: "Surface Transport" },
  { key: "property", label: "🚶 Property", description: "Indoor / Property" },
];

// Leaflet CDN — loaded once; still used by PropertyMapView
const LEAFLET_CSS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
const LEAFLET_JS  = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";

// MapLibre GL JS CDN — used by CityMapView + WorldMapView (3D buildings, OpenFreeMap tiles)
const MAPLIBRE_CSS = "https://cdnjs.cloudflare.com/ajax/libs/maplibre-gl/4.7.1/maplibre-gl.min.css";
const MAPLIBRE_JS  = "https://cdnjs.cloudflare.com/ajax/libs/maplibre-gl/4.7.1/maplibre-gl.js";

// CesiumJS CDN — lazy-loaded only when an arena/stadium venue is first selected.
// Must set window.CESIUM_BASE_URL before injecting the script so Cesium's web
// workers (cesium.js spawns them) can resolve their own URLs at runtime.
const CESIUM_VER  = "1.122";
const CESIUM_BASE = `https://cesium.com/downloads/cesiumjs/releases/${CESIUM_VER}/Build/Cesium/`;
const CESIUM_CSS  = `${CESIUM_BASE}Widgets/widgets.css`;
const CESIUM_JS   = `${CESIUM_BASE}Cesium.js`;

export default function QueueMapMonitorIndex({ active_sessions = [], ar_nodes = [], tenant_key = "cloudtraq" }) {
  const [activeScope,  setActiveScope]  = useState("city");
  const [sessions,     setSessions]     = useState(active_sessions);
  const [arNodes,      setArNodes]      = useState(ar_nodes);
  // selectedCity: "JFK" | "BOS" | "LAS" | null (null = auto-detect from sessions)
  const [selectedCity,     setSelectedCity]     = useState(null);
  // selectedProperty: "JFK_T5" | "MGM" | "SPHERE" | … | null
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [connected,      setConnected]      = useState(false);
  const [leafletReady,   setLeafletReady]   = useState(typeof window !== "undefined" && !!window.L);
  const [maplibreReady,  setMaplibreReady]  = useState(typeof window !== "undefined" && !!window.maplibregl);
  const [cesiumReady,    setCesiumReady]    = useState(typeof window !== "undefined" && !!window.Cesium);
  const [lastUpdate,     setLastUpdate]     = useState(null);
  const [animRunning,  setAnimRunning]  = useState(false);
  const [animBusy,     setAnimBusy]     = useState(false);

  // ── Demo animation controls ───────────────────────────────────────────────
  // Poll animation status every 5s so the button state stays current.
  useEffect(() => {
    let id;
    function poll() {
      fetch("/admin/queue_map_monitor/animation_status")
        .then((r) => r.json())
        .then((d) => setAnimRunning(d.running))
        .catch(() => {});
    }
    poll();
    id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, []);

  function handleStartAnimation() {
    setAnimBusy(true);
    fetch("/admin/queue_map_monitor/start_animation", {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ steps: 200, interval: 3 }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "started" || d.status === "already_running") setAnimRunning(true);
        if (d.status === "error") alert(d.message);
      })
      .catch(() => {})
      .finally(() => setAnimBusy(false));
  }

  function handleStopAnimation() {
    setAnimBusy(true);
    fetch("/admin/queue_map_monitor/stop_animation", {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
      },
    })
      .then(() => setAnimRunning(false))
      .catch(() => {})
      .finally(() => setAnimBusy(false));
  }

  // Called by WorldMapView airport label click — switches City tab to that airport's city.
  const handleCitySelect = useCallback((cityCode) => {
    setSelectedCity(cityCode);
    setActiveScope("city");
  }, []);

  // Called by CityMapView property label click — switches Property tab to that venue.
  // Also called from PropertyMapView breadcrumb × button with null to clear the focus.
  const handlePropertySelect = useCallback((propertyCode) => {
    setSelectedProperty(propertyCode);
    if (propertyCode) {
      setActiveScope("property");
    }
  }, []);

  // Called by PropertyMapView after a successful drag-to-place PATCH.
  // Updates arNodes state so React re-renders with positioned:true and confirmed lat/lng.
  const handleArNodePositioned = useCallback((nodeId, lat, lng) => {
    setArNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? { ...n, positioned: true, position: { lat, lng } }
          : n
      )
    );
  }, []);

  // Shared JourneyQueue instance registry — keyed by session id.
  // Lives outside React state to avoid re-render churn on each position update.
  const queueRegistryRef = useRef(new Map());

  // ── Load Leaflet from CDN (PropertyMapView) ───────────────────────────────
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }

    const link  = document.createElement("link");
    link.rel    = "stylesheet";
    link.href   = LEAFLET_CSS;
    document.head.appendChild(link);

    const script   = document.createElement("script");
    script.src     = LEAFLET_JS;
    script.onload  = () => setLeafletReady(true);
    script.onerror = () => console.error("[QueueMapMonitor] Leaflet CDN failed to load");
    document.head.appendChild(script);

    return () => { /* keep Leaflet loaded for page lifetime */ };
  }, []);

  // ── Load MapLibre GL JS from CDN (CityMapView + WorldMapView) ────────────
  useEffect(() => {
    if (window.maplibregl) { setMaplibreReady(true); return; }

    const link  = document.createElement("link");
    link.rel    = "stylesheet";
    link.href   = MAPLIBRE_CSS;
    document.head.appendChild(link);

    const script   = document.createElement("script");
    script.src     = MAPLIBRE_JS;
    script.onload  = () => setMaplibreReady(true);
    script.onerror = () => console.error("[QueueMapMonitor] MapLibre CDN failed to load");
    document.head.appendChild(script);

    return () => { /* keep MapLibre loaded for page lifetime */ };
  }, []);

  // ── Load CesiumJS from CDN (lazy — only when an arena/stadium venue is selected) ──
  //
  // CesiumJS is 5–8 MB; we defer loading until the user first clicks an arena or
  // stadium property chip in CityMapView.  window.CESIUM_BASE_URL must be set
  // BEFORE the script tag is injected — Cesium's web workers use it to resolve
  // their own asset URLs at startup.
  useEffect(() => {
    if (!isCesiumVenue(selectedProperty)) return;    // don't load until needed
    if (window.Cesium) { setCesiumReady(true); return; }

    window.CESIUM_BASE_URL = CESIUM_BASE;

    const link  = document.createElement("link");
    link.rel    = "stylesheet";
    link.href   = CESIUM_CSS;
    document.head.appendChild(link);

    const script   = document.createElement("script");
    script.src     = CESIUM_JS;
    script.onload  = () => setCesiumReady(true);
    script.onerror = () => console.error("[QueueMapMonitor] Cesium CDN failed to load");
    document.head.appendChild(script);

    return () => { /* keep Cesium loaded for page lifetime */ };
  }, [selectedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ActionCable subscription (raw WebSocket — no @rails/actioncable import needed) ──
  useEffect(() => {
    let unmounted = false;
    let retries   = 0;
    const MAX_RETRIES = 5;
    const wsRef_local = { current: null };

    function handleMessage(data) {
      // AR node deployed — add to property map immediately
      if (data.event === "AR_NODE_DEPLOYED") {
        setArNodes((prev) => {
          const exists = prev.find((n) => n.id === data.ar_node?.id);
          if (exists) return prev;
          return [...prev, { ...data.ar_node, _newlyDeployed: true }];
        });
        return;
      }
      if (data.event !== "QUEUE_SESSION_POSITION_UPDATE") return;

      setLastUpdate(new Date());

      setSessions((prev) => {
        const existing = prev.find((s) => s.id === data.session_id);
        if (existing) {
          return prev.map((s) =>
            s.id === data.session_id
              ? {
                  ...s,
                  scope:          data.scope,
                  movement_mode:  data.movement_mode,
                  position:       data.position,
                  alert_color:    data.alert_color,
                  session_status: data.session_status,
                  passenger:      { ...s.passenger, name: data.passenger_name },
                  pnr_locator:    data.pnr_locator,
                  updated_at:     data.position?.updated_at,
                }
              : s
          );
        } else {
          return [
            ...prev,
            {
              id:             data.session_id,
              scope:          data.scope,
              movement_mode:  data.movement_mode,
              position:       data.position,
              alert_color:    data.alert_color,
              session_status: data.session_status,
              passenger:      { name: data.passenger_name },
              offer:          { name: data.offer_name },
              pnr_locator:    data.pnr_locator,
              updated_at:     data.position?.updated_at,
            },
          ];
        }
      });

      // Push position update directly to the JourneyQueue(MapLibre) instance.
      // Registry entries are now { cityJq?, airJq?, propertyJq? }; fall back
      // to the entry itself in case PropertyMapView stores a bare Leaflet JQ.
      const entry = queueRegistryRef.current.get(data.session_id);
      if (entry) {
        const jq = entry.cityJq || entry.airJq || entry.propertyJq || entry;
        if (jq?.updatePosition) {
          // Air-scope positions arrive from DemoPositionSimulatorJob as linearly-
          // interpolated coordinates that sit south of the great-circle arc.
          // Apply the same snap that WorldMapView uses on initial render so the
          // plane stays on its arc after every simulator tick, not just on load.
          let pos = data.position;
          if (data.scope === "air" || data.movement_mode === "flying") {
            const arcPt = snapAirPositionToArc(pos);
            if (arcPt) pos = { ...pos, lat: arcPt[1], lng: arcPt[0] };
          }
          jq.updatePosition(pos, data.alert_color);
        }
      }
    }

    function connect() {
      if (unmounted) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${window.location.host}/cable`);
      wsRef_local.current = ws;

      ws.onopen = () => {
        retries = 0;
        setConnected(true);
        ws.send(JSON.stringify({
          command:    "subscribe",
          identifier: JSON.stringify({ channel: "OpsMonitorChannel", tenant_key }),
        }));
      };

      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.type === "ping") return;
          if (d.message) handleMessage(d.message);
        } catch { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        setConnected(false);
        if (unmounted) return;
        if (retries < MAX_RETRIES) {
          retries += 1;
          setTimeout(connect, 2000 * retries);
        }
      };

      ws.onerror = () => {
        setConnected(false);
      };
    }

    connect();
    return () => {
      unmounted = true;
      wsRef_local.current?.close();
    };
  }, [tenant_key]);

  // ── Session counts per scope ───────────────────────────────────────────────
  const scopeCounts = SCOPES.reduce((acc, s) => {
    acc[s.key] = sessions.filter((sess) => sess.scope === s.key).length;
    return acc;
  }, {});

  const totalActive = sessions.length;

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 0px)", background: "#07090F",
      fontFamily: "system-ui, sans-serif",
    }}>
      {/* Global CSS: JourneyQueue marker pulse + Leaflet dark-mode popup overrides */}
      <style>{`
        @keyframes jq-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.7; }
          50%       { transform: scale(1.8); opacity: 0;   }
        }
        .jq-pulse-ring {
          position: absolute; inset: -7px; border-radius: 50%;
          border: 2px solid currentColor;
          animation: jq-pulse 2.2s ease-in-out infinite;
          pointer-events: none;
        }
        .jq-marker-wrap {
          position: relative; border-radius: 50%;
          background: rgba(7,9,15,0.82);
          border: 2px solid currentColor;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.6);
        }

        /* ── Leaflet popup dark mode (PropertyMapView) ───────────────────── */
        .leaflet-popup-content-wrapper {
          background: #0F1629 !important;
          color: #E0E0F0 !important;
          border: 1px solid #1E2D52 !important;
          border-radius: 10px !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.8) !important;
          padding: 0 !important;
        }
        .leaflet-popup-content {
          margin: 12px 14px !important;
          color: #E0E0F0 !important;
        }
        .leaflet-popup-tip {
          background: #0F1629 !important;
          box-shadow: none !important;
        }
        .leaflet-popup-tip-container { filter: drop-shadow(0 2px 4px rgba(0,0,0,0.6)); }
        .leaflet-popup-close-button {
          color: #4B5563 !important;
          font-size: 18px !important;
          right: 8px !important; top: 6px !important;
        }
        .leaflet-popup-close-button:hover { color: #9CA3AF !important; }

        /* ── Leaflet tooltip / anchor labels dark mode ────────────────────── */
        .jq-anchor-label {
          background: rgba(7,9,15,0.88) !important;
          border: 1px solid #374151 !important;
          color: #A5B4FC !important;
          border-radius: 5px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          font-family: system-ui, sans-serif !important;
          box-shadow: 0 1px 6px rgba(0,0,0,0.6) !important;
        }
        .jq-anchor-label::before { display: none !important; }
      `}</style>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 20px",
        borderBottom: "1px solid #1E2D52",
        background: "#0F1629",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 20 }}>🗺️</span>
          <div>
            <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#E0E0F0" }}>
              Journey Map Monitor
            </h1>
            <p style={{ margin: 0, fontSize: 11, color: "#6B7280" }}>
              Live passenger positions · MapLibre GL + OSRM · ADR-002 compliant
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Connection badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: connected ? "#10B981" : "#6B7280",
              boxShadow: connected ? "0 0 6px #10B981" : "none",
            }} />
            <span style={{ color: connected ? "#10B981" : "#6B7280" }}>
              {connected ? "Live" : "Connecting…"}
            </span>
          </div>

          {/* Demo animation controls */}
          {animRunning ? (
            <button
              onClick={handleStopAnimation}
              disabled={animBusy}
              style={{
                background: "none", border: "1px solid #EF4444", color: "#EF4444",
                borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600,
                cursor: animBusy ? "default" : "pointer", opacity: animBusy ? 0.5 : 1,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              ⏹ Stop Demo
            </button>
          ) : (
            <button
              onClick={handleStartAnimation}
              disabled={animBusy}
              style={{
                background: "none", border: "1px solid #10B981", color: "#10B981",
                borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600,
                cursor: animBusy ? "default" : "pointer", opacity: animBusy ? 0.5 : 1,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              ▶ Animate Demo
            </button>
          )}

          {/* Total count */}
          <div style={{
            background: "#1E2D52", borderRadius: 20, padding: "3px 12px",
            fontSize: 12, fontWeight: 700, color: "#E0E0F0",
          }}>
            {totalActive} active
          </div>

          {lastUpdate && (
            <div style={{ fontSize: 10, color: "#4A5568" }}>
              Updated {lastUpdate.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* ── Scope tabs ─────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", borderBottom: "1px solid #1E2D52",
        background: "#0F1629", flexShrink: 0,
      }}>
        {SCOPES.map((scope) => {
          const isActive = activeScope === scope.key;
          const count    = scopeCounts[scope.key] || 0;
          return (
            <button
              key={scope.key}
              onClick={() => setActiveScope(scope.key)}
              style={{
                flex: 1, padding: "10px 16px",
                background: "none", border: "none",
                borderBottom: isActive ? "2px solid #7C3AED" : "2px solid transparent",
                color: isActive ? "#C4B5FD" : "#6B7280",
                fontWeight: isActive ? 700 : 400,
                fontSize: 13, cursor: "pointer",
                transition: "all 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              <span>{scope.label}</span>
              {count > 0 && (
                <span style={{
                  background: isActive ? "#7C3AED" : "#2D3A5A",
                  color: isActive ? "#fff" : "#9CA3AF",
                  borderRadius: 10, padding: "1px 7px",
                  fontSize: 11, fontWeight: 700,
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Map content ────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
        {/* Gate on both map libraries: MapLibre for city/world, Leaflet for property */}
        {!leafletReady || !maplibreReady ? (
          <LoadingState leaflet={leafletReady} maplibre={maplibreReady} />
        ) : (
          <>
            {/* Keep all three maps mounted to preserve MapLibre/Leaflet state across tab switches.
                isActive triggers resize() so tiles fill the container when revealed. */}
            <div style={{ flex: 1, display: activeScope === "air"      ? "flex" : "none" }}>
              <WorldMapView
                sessions={sessions}
                queueRegistryRef={queueRegistryRef}
                isActive={activeScope === "air"}
                onCitySelect={handleCitySelect}
              />
            </div>
            <div style={{ flex: 1, display: activeScope === "city"     ? "flex" : "none" }}>
              <CityMapView
                sessions={sessions}
                queueRegistryRef={queueRegistryRef}
                isActive={activeScope === "city"}
                selectedCity={selectedCity}
                onCitySelect={handleCitySelect}
                onPropertySelect={handlePropertySelect}
              />
            </div>
            {/* Property tab — always mounted; inner views toggled by CSS.
                isCesiumVenue() determines which sub-view is active:
                  arena/stadium  →  CesiumPropertyView (3-D Cesium ion)
                  hotel/expo/convention → PropertyMapView (2-D Leaflet) */}
            <div style={{ flex: 1, display: activeScope === "property" ? "flex" : "none" }}>
              {/* Phase C: 3-D Cesium viewer for arena / stadium venues */}
              <div style={{
                flex: 1,
                display: isCesiumVenue(selectedProperty) ? "flex" : "none",
              }}>
                <CesiumPropertyView
                  sessions={sessions}
                  queueRegistryRef={queueRegistryRef}
                  isActive={activeScope === "property" && isCesiumVenue(selectedProperty)}
                  selectedProperty={selectedProperty}
                  cesiumReady={cesiumReady}
                />
              </div>

              {/* 2-D Leaflet view for hotel / expo / convention venues (and default) */}
              <div style={{
                flex: 1,
                display: !isCesiumVenue(selectedProperty) ? "flex" : "none",
              }}>
                <PropertyMapView
                  sessions={sessions}
                  arNodes={arNodes}
                  onArNodePositioned={handleArNodePositioned}
                  queueRegistryRef={queueRegistryRef}
                  isActive={activeScope === "property" && !isCesiumVenue(selectedProperty)}
                  selectedProperty={selectedProperty}
                  onPropertySelect={handlePropertySelect}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div style={{
        padding: "6px 20px",
        borderTop: "1px solid #1E2D52",
        background: "#0F1629",
        display: "flex", gap: 16,
        fontSize: 10, color: "#4A5568",
        flexShrink: 0,
      }}>
        <span>City/World: MapLibre GL JS + OpenFreeMap liberty</span>
        <span>Property: Leaflet + OSM</span>
        <span>Routing: OSRM public (ADR-002 compliant)</span>
        <span>3D Venues: Cesium ion 1.122 (arenas/stadiums)</span>
        <span style={{ marginLeft: "auto" }}>
          Phase 2: DWN selective disclosure · Cisco Meraki IPS · Cesium floor plans
        </span>
      </div>
    </div>
  );
}

function LoadingState({ leaflet, maplibre }) {
  const missing = [];
  if (!leaflet)   missing.push("Leaflet");
  if (!maplibre)  missing.push("MapLibre GL");
  return (
    <div style={{
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      background: "#07090F", color: "#6B7280", flexDirection: "column", gap: 12,
    }}>
      <div style={{ fontSize: 32 }}>🗺️</div>
      <div style={{ fontSize: 13 }}>Loading {missing.join(" + ")}…</div>
      <div style={{ fontSize: 10, color: "#374151" }}>Fetching map libraries from CDN</div>
    </div>
  );
}
